import { useTranslation } from 'react-i18next'
import React, { useMemo } from 'react'
import { ReactComponent as NoClientsImage } from 'images/illustration/no-clients.svg'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Grid, Typography } from 'gu-ui-kit'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'components/EmptyResults/EmptyResults.module.scss'

const IMAGE_SIZE_MAP = {
  s: 48,
  m: 72,
  l: 96,
}

const ICON_SIZE_MAP = {
  s: 32,
  m: 40,
  l: 64,
}

const EmptyResults = ({
  header = '',
  text = '',
  className = '',
  image = <NoClientsImage className={styles['empty-results__image']} />,
  icon = null,
  actionButton = null,
}) => {
  const breakpoints = useBreakpoint()
  const { t } = useTranslation()

  const size = useMemo(() => {
    return !breakpoints.fb ? 's' : !breakpoints.lg ? 'm' : 'l'
  }, [breakpoints])

  const emptyIconProps = useMemo(() => {
    return {
      image: {
        width: IMAGE_SIZE_MAP[size],
        height: IMAGE_SIZE_MAP[size],
      },
      icon: {
        width: ICON_SIZE_MAP[size],
        height: ICON_SIZE_MAP[size],
      },
    }
  }, [size])

  const fonts = useMemo(() => {
    if (!breakpoints.fb) {
      return {
        header: 'Header-30',
        title: 'Body-15',
      }
    } else if (!breakpoints.lg) {
      return {
        header: 'Header-40',
        title: 'Body-20',
      }
    } else {
      return {
        header: 'Header-50',
        title: 'Body-25',
      }
    }
  }, [breakpoints])

  return (
    <Grid
      alignItems={'center'}
      direction={'column'}
      className={clsx(className, styles['empty-results'])}
    >
      {icon ? (
        <Grid
          fluid={false}
          alignItems={'center'}
          justifyContent={'center'}
          className={clsx(styles['empty-results__icon'], {
            [styles[`empty-results__icon_size_${size}`]]: size,
          })}
        >
          {React.cloneElement(icon, emptyIconProps.icon)}
        </Grid>
      ) : (
        React.cloneElement(image, emptyIconProps.image)
      )}
      <Typography
        className={clsx('margin-top-32', 'margin-bottom-16', 'margin-left-16', 'margin-right-16')}
        variant={fonts.header}
        color={'Label-Primary'}
      >
        {header || t('common::empty_results.header')}
      </Typography>
      <Typography
        variant={fonts.title}
        color={'Label-Fifth'}
        noMargin
        dangerouslySetInnerHTML={{ __html: text || t('common::empty_results.text') }}
      />
      {actionButton && (
        <Grid
          justifyContent={'center'}
          className={'margin-top-32'}
        >
          {actionButton}
        </Grid>
      )}
    </Grid>
  )
}

EmptyResults.propTypes = {
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.oneOf([null])]),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.oneOf([null])]),
  className: PropTypes.string,
  image: PropTypes.element,
  icon: PropTypes.element,
  actionButton: PropTypes.oneOfType([PropTypes.element, PropTypes.oneOf([null])]),
}

export default EmptyResults
